import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle,
  Eye,
  FileText,
  Flag,
  MessageSquare,
  User,
  X,
  Users,
  Clock,
  Shield,
  Ban,
  Trash2,
  Archive,
  Save,
  Edit3,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { adminApi } from "../utils/admin-api-axios";

export const ReportDetail = ({
  report,
  isOpen,
  onClose,
  onResolve,
  onRefresh,
}) => {
  const [adminNotes, setAdminNotes] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [actionComment, setActionComment] = useState("");
  const [banDuration, setBanDuration] = useState("24");
  const [loading, setLoading] = useState(false);
  const [resolveAllDuplicates, setResolveAllDuplicates] = useState(true);
  const [notesLoading, setNotesLoading] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [hasUnsavedNotes, setHasUnsavedNotes] = useState(false);

  console.log("Report", report);
  
  const navigate = useNavigate();

  // Reset state when report changes
  useEffect(() => {
    if (report) {
      setAdminNotes(report.adminNotes || "");
      setSelectedAction("");
      setActionComment("");
      setBanDuration("24");
      setResolveAllDuplicates(true);
      setIsEditingNotes(false);
      setHasUnsavedNotes(false);
    }
  }, [report]);

  // Track changes to admin notes
  useEffect(() => {
    if (report) {
      setHasUnsavedNotes(adminNotes !== (report.adminNotes || ""));
    }
  }, [adminNotes, report]);

  if (!isOpen || !report) return null;

  const getTypeIcon = (type) => {
    switch (type.toLowerCase()) {
      case "user":
        return <User className="w-4 h-4" />;
      case "chat":
        return <MessageSquare className="w-4 h-4" />;
      case "listing":
        return <FileText className="w-4 h-4" />;
      default:
        return <Flag className="w-4 h-4" />;
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "warning":
        return <AlertTriangle className="w-4 h-4" />;
      case "temporary_ban":
        return <Clock className="w-4 h-4" />;
      case "permanent_ban":
        return <Ban className="w-4 h-4" />;
      case "remove_listing":
        return <Trash2 className="w-4 h-4" />;
      case "deactivate_listing":
        return <Archive className="w-4 h-4" />;
      case "flag_conversation":
        return <Flag className="w-4 h-4" />;
      case "archive_conversation":
        return <Archive className="w-4 h-4" />;
      case "delete_conversation":
        return <Trash2 className="w-4 h-4" />;
      default:
        return <Shield className="w-4 h-4" />;
    }
  };

  const getAvailableActions = () => {
    const actions = [];
    
    switch (report.type.toLowerCase()) {
      case "user":
        actions.push(
          { value: "warning", label: "Issue Warning" },
          { value: "temporary_ban", label: "Temporary Ban" },
          { value: "permanent_ban", label: "Permanent Ban" }
        );
        break;
      case "listing":
        actions.push(
          { value: "warning", label: "Warn Seller" },
          { value: "deactivate_listing", label: "Deactivate Listing" },
          { value: "remove_listing", label: "Remove Listing" },
          { value: "temporary_ban", label: "Ban Seller (Temporary)" },
          { value: "permanent_ban", label: "Ban Seller (Permanent)" }
        );
        break;
      case "chat":
        actions.push(
          { value: "warning", label: "Warn User" },
          { value: "flag_conversation", label: "Flag Conversation" },
          { value: "archive_conversation", label: "Archive Conversation" },
          { value: "delete_conversation", label: "Delete Conversation" },
          { value: "temporary_ban", label: "Ban User (Temporary)" },
          { value: "permanent_ban", label: "Ban User (Permanent)" }
        );
        break;
    }
    
    return actions;
  };

  const handleSaveNotes = async () => {
    try {
      setNotesLoading(true);
      
      const data = await adminApi.updateReport(report.id, {
        adminNotes: adminNotes.trim()
      });

      if (data.success) {
        setHasUnsavedNotes(false);
        setIsEditingNotes(false);
        // Update the report object to reflect the change
        report.adminNotes = adminNotes.trim();
        // Refresh the parent list to reflect changes
        onRefresh();
      } else {
        throw new Error(data.message || "Failed to save notes");
      }
    } catch (error) {
      console.error("Error saving notes:", error);
      alert(error.message || "Failed to save notes");
    } finally {
      setNotesLoading(false);
    }
  };

  const handleTakeAction = async () => {
    if (!selectedAction || !actionComment.trim()) {
      alert("Please select an action and provide a comment.");
      return;
    }

    try {
      setLoading(true);

      const updateData = {
        adminNotes: adminNotes.trim(),
        action: selectedAction,
        actionComment: actionComment.trim(),
        resolveAllDuplicates: resolveAllDuplicates && report.isGrouped,
      };

      // Add ban duration for temporary bans
      if (selectedAction === "temporary_ban") {
        updateData.banDuration = banDuration;
      }

      const data = await adminApi.updateReport(report.id, updateData);

      if (data.success) {
        onRefresh();
        onClose();
      } else {
        throw new Error(data.message || "Failed to take action");
      }
    } catch (error) {
      console.error("Error taking action:", error);
      alert(error.message || "Failed to take action");
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      setLoading(true);
      
      const data = await adminApi.dismissReport(report.id, {
        adminNotes: adminNotes.trim() || "Dismissed by admin - no action needed",
        resolveAllDuplicates: resolveAllDuplicates && report.isGrouped,
      });

      if (data.success) {
        onRefresh();
        onClose();
      } else {
        throw new Error(data.message || "Failed to dismiss report");
      }
    } catch (error) {
      console.error("Error dismissing report:", error);
      alert(error.message || "Failed to dismiss report");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkResolve = async () => {
    if (!report.isGrouped) return;

    try {
      setLoading(true);
      
      const data = await adminApi.bulkResolveReports({
        targetId: report.targetId,
        type: report.targetType,
        resolutionNote: adminNotes.trim() || "Bulk resolved by admin"
      });

      if (data.success) {
        onRefresh();
        onClose();
      }
    } catch (error) {
      console.error("Error bulk resolving:", error);
      alert(error.message || "Failed to bulk resolve");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDismiss = async () => {
    if (!report.isGrouped) return;

    try {
      setLoading(true);
      
      const data = await adminApi.bulkResolveReports({
        targetId: report.targetId,
        type: report.targetType,
        resolutionNote: adminNotes.trim() || "Bulk dismissed by admin",
        isDismissal: true
      });

      if (data.success) {
        onRefresh();
        onClose();
      }
    } catch (error) {
      console.error("Error bulk dismissing:", error);
      alert(error.message || "Failed to bulk dismiss");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            Report Details
            {report.isGrouped && report.reportCount > 1 && (
              <Badge className="ml-2">
                {report.reportCount} reports
              </Badge>
            )}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Report Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getTypeIcon(report.type)}
              <div>
                <h3 className="font-semibold text-lg">{report.id.slice(-8)}</h3>
                <p className="text-sm text-gray-600">{report.reason}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge
                variant={
                  report.priority === "High"
                    ? "destructive"
                    : report.priority === "Medium"
                    ? "default"
                    : "secondary"
                }
              >
                {report.priority} Priority
              </Badge>
              <Badge
                variant={
                  report.status === "Resolved" 
                    ? "default" 
                    : report.status === "Dismissed"
                    ? "secondary"
                    : "destructive"
                }
              >
                {report.status}
              </Badge>
            </div>
          </div>

          {/* Multiple Reports Warning */}
          {report.isGrouped && report.reportCount > 1 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="!p-4">
                <div className="flex items-center space-x-2 text-orange-800">
                  <Users className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Multiple Reports</p>
                    <p className="text-sm">
                      This {report.type.toLowerCase()} has been reported {report.reportCount} times.
                      Taking action will affect all related reports.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Report Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Reported By</p>
                  <p className="text-sm text-gray-600">{report.reportedBy}</p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Reported User</p>
                  <p className="text-sm text-gray-600">{report.reportedUser}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Report Date</p>
                  <p className="text-sm text-gray-600">{report.date}</p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Flag className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Report Type</p>
                  <p className="text-sm text-gray-600">{report.type}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Related Content */}
          {(report.type === "Listing" ||
            report.type === "Chat" ||
            report.type === "User") && (
            <div className="space-y-2">
              <h4 className="font-medium">Related Content</h4>
              <div className="bg-gray-50 p-3 rounded-md">
                {report.type === "Listing" && report.reportedListing && (
                  <div className="flex justify-between items-center">
                    <p className="text-sm">
                      <span className="font-medium">Listing ID:</span>{" "}
                      {report.reportedListing}
                    </p>
                    <a
                      href={`/listing/${report.reportedListing}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </a>
                  </div>
                )}
                {report.type === "Chat" && report.reportedChat && (
                  <div className="flex justify-between items-center">
                    <p className="text-sm">
                      <span className="font-medium">Chat ID:</span>{" "}
                      {report.reportedChat}
                    </p>
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(`/admin/chat`, {
                          state: { chatId: report.reportedChat },
                        })
                      }
                    >
                      View Chat
                    </Button>
                  </div>
                )}
                {report.type === "User" && report.reportedUser && (
                  <div className="flex justify-between items-center">
                    <p className="text-sm">
                      <span className="font-medium">User:</span>{" "}
                      {report.reportedUser}
                    </p>
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(`/admin/users`, {
                          state: { selectedUserId: report.reportedUserId },
                        })
                      }
                    >
                      View User
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Previous Actions */}
          {report.actionsTaken && report.actionsTaken.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium">Previous Actions</h4>
              <div className="space-y-2">
                {report.actionsTaken.map((action, index) => (
                  <div key={index} className="bg-blue-50 p-3 rounded-md">
                    <div className="flex items-center space-x-2 mb-1">
                      {getActionIcon(action.action)}
                      <span className="font-medium text-sm">
                        {action.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(action.at).toLocaleString()}
                      </span>
                    </div>
                    {action.comment && (
                      <p className="text-sm text-gray-700 ml-6">{action.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <h4 className="font-medium">Description</h4>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-700">{report.description}</p>
            </div>
          </div>

          {/* Admin Notes - Enhanced */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Admin Notes</h4>
              <div className="flex items-center space-x-2">
                {hasUnsavedNotes && (
                  <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">
                    Unsaved changes
                  </span>
                )}
                {!isEditingNotes && adminNotes.trim() && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingNotes(true)}
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                )}
                {(hasUnsavedNotes || isEditingNotes) && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSaveNotes}
                    disabled={notesLoading || !adminNotes.trim()}
                  >
                    {notesLoading ? (
                      <>
                        <div className="w-4 h-4 mr-1 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-1" />
                        Save Notes
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
            
            {isEditingNotes || !adminNotes.trim() ? (
              <Textarea
                placeholder="Add your notes about this report..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
              />
            ) : (
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {adminNotes || "No admin notes added yet."}
                </p>
              </div>
            )}
          </div>

          {/* Action Selection */}
          {report.status === "Pending" && (
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium">Take Action</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Action</label>
                  <Select value={selectedAction} onValueChange={setSelectedAction}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an action..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableActions().map((action) => (
                        <SelectItem key={action.value} value={action.value}>
                          <div className="flex items-center space-x-2">
                            {getActionIcon(action.value)}
                            <span>{action.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedAction === "temporary_ban" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Ban Duration (hours)</label>
                    <Input
                      type="number"
                      value={banDuration}
                      onChange={(e) => setBanDuration(e.target.value)}
                      min="1"
                      max="8760"
                      placeholder="24"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Action Comment *</label>
                <Textarea
                  placeholder="Explain the reason for this action..."
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                  rows={2}
                  required
                />
              </div>

              {report.isGrouped && report.reportCount > 1 && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="resolveAll"
                    checked={resolveAllDuplicates}
                    onChange={(e) => setResolveAllDuplicates(e.target.checked)}
                  />
                  <label htmlFor="resolveAll" className="text-sm">
                    Apply to all {report.reportCount} reports for this {report.type.toLowerCase()}
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Close
            </Button>
            
            {report.status === "Pending" && (
              <>
                <Button
                  variant="secondary"
                  onClick={handleDismiss}
                  disabled={loading}
                >
                  <X className="w-4 h-4 mr-2" />
                  Dismiss
                </Button>

                {report.isGrouped && report.reportCount > 1 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleBulkResolve}
                      disabled={loading}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Bulk Resolve ({report.reportCount})
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={handleBulkDismiss}
                      disabled={loading}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Bulk Dismiss ({report.reportCount})
                    </Button>
                  </>
                )}

                <Button
                  variant="destructive"
                  onClick={handleTakeAction}
                  disabled={loading || !selectedAction || !actionComment.trim()}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Take Action & Resolve
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};